<template>
  <service-wrapper
    title="Trychart"
    :subtitle="$t('DEALS.DEAL_PROGRESS.UNDERWRITING.TAX_DATA')"
    img="/images/trychart.svg"
    :is-success="serviceInfo.status === COMMERCIAL_DATA_STATUS_SUCCESS"
    :is-error="serviceHasUnexpectedStatus(serviceInfo.status)"
    :date="serviceInfo.date"
  >
    <div class="flex items-center space-x-1">
      <confirm-service-start
        :date="serviceInfo.date"
        :confirm-handler="startService"
        :status="serviceInfo.status"
        :service-name="$t('DEALS.DEAL_PROGRESS.UNDERWRITING.TAX_DATA')"
        :provider-name="PROVIDER_TRYCHART"
        :is-loading="loading"
      />
    </div>

    <template #content>
      <div class="space-y-6">
        <div
          v-if="COMMERCIAL_DATA_STATUS_NOT_STARTED === serviceInfo.status"
          class="w-full"
        >
          {{ $t("DEALS.SERVICES_DESCRIPTION.TRYCHART") }}
        </div>

        <service-error
          v-if="serviceHasUnexpectedStatus(serviceInfo.status)"
          :have-response="!!jsonViewerData"
          :service-error-message="serviceInfo.status"
        />

        <json-viewer-wrapper
          v-if="
            serviceInfo.status === COMMERCIAL_DATA_STATUS_SUCCESS &&
            jsonViewerData
          "
          data-cy="service-json-viewer"
          :data="jsonViewerData"
          :provider-name="PROVIDER_TRYCHART"
          :service-name="$t('DEALS.DEAL_PROGRESS.UNDERWRITING.TAX_DATA')"
          :service-type="$t('DEALS.DEAL_PROGRESS.UNDERWRITING.TAX_DATA')"
        />

        <div
          v-else-if="
            !serviceHasUnexpectedStatus(serviceInfo.status) &&
            !statusSuccessOrNotStarted
          "
          class="flex items-center justify-center"
        >
          <span>{{ serviceInfo.status ?? "-" }}</span>
        </div>
      </div>
    </template>
    <widget-link v-if="serviceInfo.data?.url" :service="PROVIDER_TRYCHART" />
  </service-wrapper>
</template>

<script setup lang="ts">
import { watchImmediate } from "@vueuse/core";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import {
  COMMERCIAL_DATA_STATUS_NOT_STARTED,
  COMMERCIAL_DATA_STATUS_SUCCESS,
  PROVIDER_TRYCHART,
} from "@/helpers/constants";
import { serviceHasUnexpectedStatus } from "@/helpers/services";
import { loadTryChartScript } from "@/helpers/thirdPartyService";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { getAsyncComponent, usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import servicesApiService from "@/services/modules/services";
import { useJsonTreeStateStore } from "@/stores/serviceJsonViewer";

import WidgetLink from "@/components/WidgetLink.vue";
import ServiceWrapper from "@/components/deals/dataServices/ServiceWrapper.vue";
import JsonViewerWrapper from "@/components/deals/dataServices/jsonViewer/JsonViewerWrapper.vue";
import ConfirmServiceStart from "@/views/businessCreditDeals/components/ConfirmServiceStart.vue";

const ServiceError = getAsyncComponent(
  () => import("@/components/deals/dataServices/ServiceError.vue")
);

const { activeDeal } = useDeals();
const { dispatch } = useStore();
const serviceJsonTreeStateStore = useJsonTreeStateStore();
const { isJsonTreeShown } = storeToRefs(useJsonTreeStateStore());
const copyToClipboard = useClipboardWithMessage();
const { showMessage } = useNotification();

const tryChartIframe = ref<{ open: () => void; close: () => void }>();
const serviceRunInfo = ref({
  code: "",
  error: "",
});

const serviceInfo = computed(() => {
  const { statuses, dates, try_chart } = activeDeal.value.commercialData || {};
  return {
    status: statuses?.try_chart || "",
    date: dates?.try_chart || "",
    data: try_chart,
  };
});

const jsonViewerData = computed(() => {
  return serviceInfo.value.data?.url ? null : (serviceInfo.value.data ?? null);
});

const urlToCopy = computed(() => {
  return serviceInfo.value.data?.url;
});

const statusSuccessOrNotStarted = computed(() =>
  [COMMERCIAL_DATA_STATUS_NOT_STARTED, COMMERCIAL_DATA_STATUS_SUCCESS].includes(
    serviceInfo.value.status
  )
);

const getClientId = async () => {
  const clientId = await servicesApiService.getTryChartClientId();
  return clientId;
};

const runService = async (payload: {
  provider: string;
  options?: { code: string };
}) => {
  await dispatch("applications/enrichApp", payload);
};

const { fetchWrapper: startService, loading } = usePromiseWrapper(async () => {
  if (serviceInfo.value.data) {
    await runService({ provider: PROVIDER_TRYCHART });
    return;
  }

  const clientId = await getClientId();
  await handleTryChartIframe(clientId);
});

const handleTryChartIframe = async (clientId: string) => {
  const params = {
    clientId,
    flow: "irs-online-account",
    onSuccess,
    onError,
    onClose,
  };
  tryChartIframe.value = window.ChartConnect?.initialize(params);
  tryChartIframe.value?.open();
};

const onSuccess = async ({ code }: { code: string }) => {
  serviceRunInfo.value.code = code;
};

const onError = async ({ errorMessage }: { errorMessage: string }) => {
  tryChartIframe.value?.close();
  serviceRunInfo.value.error = errorMessage;
  showMessage(errorMessage, "error");
};

const onClose = () => {
  return;
};

watchImmediate(
  () => serviceInfo.value.status,
  () => {
    if (!isJsonTreeShown.value(PROVIDER_TRYCHART) && serviceInfo.value.data) {
      serviceJsonTreeStateStore.updateServiceJsonStatus(PROVIDER_TRYCHART);
    }
  }
);

watch(urlToCopy, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    copyToClipboard(newValue);
  }
});

watch(
  () => serviceRunInfo.value.code,
  async (updatedValue, oldValue) => {
    if (updatedValue && updatedValue !== oldValue) {
      await runService({
        provider: PROVIDER_TRYCHART,
        options: { code: updatedValue },
      });
    }
  }
);

watch(
  () => serviceRunInfo.value.error,
  async (updatedValue, oldValue) => {
    if (updatedValue && updatedValue !== oldValue) {
      await runService({ provider: PROVIDER_TRYCHART });
    }
  }
);

onBeforeMount(async () => {
  await loadTryChartScript();
});
</script>
<style>
@reference "@/assets/tailwind.css";

#chart-connect-iframe {
  @apply !left-1/2 !w-1/2 !-translate-x-1/2;
}
</style>
